/**
 * テナント/雇用形態ページ (/tenant/employment-type)
 *
 * CompanySettingsPage から employment-type タブを切り出したもの。
 * サイドバーメニュー「テナント」配下のタブとして表示される。
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchEmploymentTypes } from "../api/employmentTypes";
import PageHeader from "../components/PageHeader";
import { TabBar } from "../components/molecules/TabBar";
import { ActionIcon } from "../components/atoms/Icon";
import {
    ColumnFilterPopup,
    useColumnFilter,
    type SortDirection,
} from "../components/molecules/ColumnFilterPopup";
import { useMenuTitle } from "../shared/menu/useMenuTitle";
import { AddIcon as PlusIcon } from "../components/atoms/Icon";
import { Button } from "../components/atoms/Button/Button";
import {
    CommonTable,
    CommonTableHeaderCell,
    CommonTableEditHeaderCell,
    CommonTableRow,
    CommonTableCell,
    CommonTableEditCell,
} from "../components/table";

const UploadIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className={className}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 7.5 7.5 12M12 7.5v9" />
    </svg>
);

interface EmploymentTypeRow {
    id: number | null;
    code: string;
    name: string;
    isNew: boolean;
}

const EMPLOYMENT_TYPE_COLUMN_COUNT = 3;
const EMPTY_VALUE_KEY = "__empty__";
const EMPTY_VALUE_LABEL = "(未設定)";

type SortColumn = "code" | "name";

const displayValue = (val: string | null | undefined) =>
    val === null || val === undefined || val === "" ? "-" : val;

const toKey = (val: string | null | undefined): string =>
    val === null || val === undefined || val === "" ? EMPTY_VALUE_KEY : val;

const labelOrEmpty = (key: string): string =>
    key === EMPTY_VALUE_KEY ? EMPTY_VALUE_LABEL : key;

const TenantEmploymentTypePage = () => {
    const menuTitle = useMenuTitle();
    const navigate = useNavigate();

    const [etRows, setEtRows] = useState<EmploymentTypeRow[]>([]);
    const [etLoading, setEtLoading] = useState(false);

    useEffect(() => {
        const loadEmploymentTypes = async () => {
            setEtLoading(true);
            try {
                const data = await fetchEmploymentTypes();
                setEtRows(
                    data.items.map((item) => ({
                        id: item.id,
                        code: item.code,
                        name: item.name,
                        isNew: false,
                    })),
                );
            } catch (error) {
                console.error("Failed to fetch employment types:", error);
            } finally {
                setEtLoading(false);
            }
        };
        loadEmploymentTypes();
    }, []);

    // 列ごとの並び替え/絞り込み state
    const codeFilter = useColumnFilter(etRows, (r) => toKey(r.code), labelOrEmpty);
    const nameFilter = useColumnFilter(etRows, (r) => toKey(r.name), labelOrEmpty);

    // 並び替えは「同時に1列のみ」アクティブ
    const [activeSort, setActiveSort] = useState<{
        column: SortColumn;
        direction: "asc" | "desc";
    } | null>(null);

    const sortFor = (column: SortColumn): SortDirection =>
        activeSort?.column === column ? activeSort.direction : null;

    const handleSortChange = useCallback(
        (column: SortColumn) => (dir: SortDirection) => {
            setActiveSort(dir === null ? null : { column, direction: dir });
        },
        [],
    );

    // 表示用にソート/絞り込みを適用
    const displayedRows = useMemo(() => {
        let list = etRows;

        if (codeFilter.isFiltering) {
            const set = new Set(codeFilter.selected);
            list = list.filter((r) => set.has(toKey(r.code)));
        }
        if (nameFilter.isFiltering) {
            const set = new Set(nameFilter.selected);
            list = list.filter((r) => set.has(toKey(r.name)));
        }

        if (activeSort) {
            const cmpCode = (a: EmploymentTypeRow, b: EmploymentTypeRow): number =>
                (a.code ?? "").localeCompare(b.code ?? "", "ja");
            const cmpName = (a: EmploymentTypeRow, b: EmploymentTypeRow): number =>
                (a.name ?? "").localeCompare(b.name ?? "", "ja");
            const cmp = activeSort.column === "code" ? cmpCode : cmpName;
            list = [...list].sort((a, b) =>
                activeSort.direction === "asc" ? cmp(a, b) : -cmp(a, b),
            );
        }

        return list;
    }, [
        etRows,
        codeFilter.isFiltering,
        codeFilter.selected,
        nameFilter.isFiltering,
        nameFilter.selected,
        activeSort,
    ]);

    // ヘッダークリック時、ポップアップは「同時に1つだけ」開く
    const openOnly = useCallback(
        (which: SortColumn, anchor: HTMLButtonElement) => {
            const map = {
                code: codeFilter,
                name: nameFilter,
            } as const;
            const target = map[which];
            target.anchorRef.current = anchor;
            const willOpen = !target.open;
            target.setOpen(willOpen);
            if (willOpen) {
                Object.entries(map).forEach(([col, f]) => {
                    if (col !== which) f.setOpen(false);
                });
            }
        },
        [codeFilter, nameFilter],
    );

    return (
            <div className="flex flex-1 flex-col overflow-hidden">
                <PageHeader title={menuTitle} />
                <TabBar parentCode="tenant" />

                {/* アクションボタン */}
                <div className="px-6 pb-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => navigate("/tenant/employment-type/new")}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500 text-white text-base font-medium hover:bg-brand-600 transition-colors"
                        >
                            <ActionIcon name="add" className="w-4 h-4" />
                            新規登録
                        </button>
                        <button
                            type="button"
                            disabled
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-300 text-gray-500 text-base font-medium cursor-not-allowed"
                        >
                            <ActionIcon name="edit" className="w-4 h-4" />
                            一括編集
                        </button>
                        <button
                            type="button"
                            disabled
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-300 text-gray-500 text-base font-medium cursor-not-allowed"
                        >
                            <ActionIcon name="upload" className="w-4 h-4" />
                            アップロード
                        </button>
                        <button
                            type="button"
                            disabled
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-300 text-gray-500 text-base font-medium cursor-not-allowed"
                        >
                            <ActionIcon name="download" className="w-4 h-4" />
                            ダウンロード
                        </button>
                    </div>
                </div>

                {/* 雇用形態テーブル */}
                <div className="flex-1 min-h-0 px-6 pb-6">
                    <CommonTable>
                        <thead className="sticky top-0 z-20">
                            <tr>
                                <CommonTableEditHeaderCell />
                                <CommonTableHeaderCell
                                    sortable
                                    sortActive={codeFilter.open}
                                    sortDirection={sortFor("code")}
                                    filterCount={codeFilter.filterCount}
                                    onSort={(anchor) => openOnly("code", anchor)}
                                    className="min-w-[200px]"
                                >
                                    雇用形態コード
                                </CommonTableHeaderCell>
                                <CommonTableHeaderCell
                                    sortable
                                    sortActive={nameFilter.open}
                                    sortDirection={sortFor("name")}
                                    filterCount={nameFilter.filterCount}
                                    onSort={(anchor) => openOnly("name", anchor)}
                                    className="min-w-[200px]"
                                >
                                    雇用形態名
                                </CommonTableHeaderCell>
                            </tr>
                        </thead>
                        <tbody>
                            {displayedRows.map((row) => (
                                <CommonTableRow key={row.id ?? `new-${row.code}`}>
                                    <CommonTableEditCell
                                        onClick={() => navigate(`/tenant/employment-type/${row.id}/edit`)}
                                    />
                                    <CommonTableCell>{displayValue(row.code)}</CommonTableCell>
                                    <CommonTableCell>{displayValue(row.name)}</CommonTableCell>
                                </CommonTableRow>
                            ))}
                            {displayedRows.length === 0 && (
                                <CommonTableRow>
                                    <CommonTableCell colSpan={EMPLOYMENT_TYPE_COLUMN_COUNT} className="py-8 text-gray-500">
                                        {etLoading
                                            ? "読み込み中..."
                                            : etRows.length === 0
                                                ? "雇用形態が登録されていません"
                                                : "該当する雇用形態がありません"}
                                    </CommonTableCell>
                                </CommonTableRow>
                            )}
                        </tbody>
                    </CommonTable>
                </div>

                {/* 列ごとの並び替え＋絞り込みポップアップ */}
                <ColumnFilterPopup
                    isOpen={codeFilter.open}
                    onClose={() => codeFilter.setOpen(false)}
                    anchorRef={codeFilter.anchorRef}
                    sortDirection={sortFor("code")}
                    onSortChange={handleSortChange("code")}
                    options={codeFilter.options}
                    selectedValues={codeFilter.selected}
                    onSelectedValuesChange={codeFilter.setSelected}
                />
                <ColumnFilterPopup
                    isOpen={nameFilter.open}
                    onClose={() => nameFilter.setOpen(false)}
                    anchorRef={nameFilter.anchorRef}
                    sortDirection={sortFor("name")}
                    onSortChange={handleSortChange("name")}
                    options={nameFilter.options}
                    selectedValues={nameFilter.selected}
                    onSelectedValuesChange={nameFilter.setSelected}
                />
            </div>
    );
};

export default TenantEmploymentTypePage;
